/**
 * Fil — le conteneur qui défile d'une conversation (famille « conversation », 01-11-2026).
 *
 *   · Il reste COLLÉ en bas : un message qui arrive, une image qui finit de charger, une bulle
 *     qui grandit — on voit le dernier. Il se décolle dès qu'on remonte (à plus de `seuilBas` du
 *     bas), et un bouton rond « revenir en bas » apparaît, avec les non-lus.
 *   · Il charge le plus ancien en haut : à moins de `seuilAncien` du haut, `onPlusAncien` est
 *     appelé (une fois, jusqu'à ce que le contenu change ou que `chargementAncien` retombe) ; une
 *     pilule « Chargement de l'historique… » flotte en haut pendant ce temps. Un fil trop court
 *     pour défiler demande aussi.
 *   · Il GARDE SA POSITION : quand des messages s'ajoutent au-dessus (ou qu'une image au-dessus
 *     charge), la rangée qu'on lisait ne bouge pas — le fil retient la première rangée visible
 *     et la remet à sa place après chaque changement de taille, avant que l'écran ne se dessine.
 *
 * `disposition` range ses bulles : tout à gauche (défaut, avatar devant) ou `deux-cotes` (le sortant à
 * droite, comme une messagerie de téléphone).
 *
 * Chaque enfant direct est une rangée (une `Bulle`, un séparateur). Le fil ne fenêtre pas : une
 * conversation de plusieurs milliers de messages garde sa fenêtre dans l'app, qui lui passe les
 * rangées visibles. `poignee` donne `allerEnBas()` et `allerA(id)` (rejoindre un message cité).
 *
 *   <Fil onPlusAncien={chargerAvant} chargementAncien={enCours} toutCharge={!reste} nonLus={3}>
 *     <SeparateurDeJour libelle="Aujourd'hui" collant />
 *     {groupes.map(({ item, position }) => <Bulle key={item.id} id={`m-${item.id}`} … />)}
 *   </Fil>
 */
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from "react";

import { Badge } from "../Badge";
import { Button } from "../Button";
import { ArrowDown } from "../icons";
import { Spinner } from "../Spinner";
import { cn } from "../utils";
import { ContexteDisposition, type DispositionDuFil } from "./disposition";
import "./conversation.css";

export interface PoigneeDuFil {
  /** Descendre au dernier message, et y rester collé. */
  allerEnBas: (doux?: boolean) => void;
  /** Rejoindre la rangée d'`id` (le DOM), au centre. `false` si elle n'est pas rendue. */
  allerA: (id: string, doux?: boolean) => boolean;
  /** L'élément qui défile. */
  element: () => HTMLDivElement | null;
}

export interface FilProps {
  children: ReactNode;
  /** Charger plus ancien (appelé près du haut). */
  onPlusAncien?: () => void;
  /** Le plus ancien est en route : la pilule de chargement, et pas de nouvel appel. */
  chargementAncien?: boolean;
  /** Il n'y a plus rien avant. */
  toutCharge?: boolean;
  /** À combien de px du haut demander plus ancien. Défaut 800. */
  seuilAncien?: number;
  /** À combien de px du bas on est encore « collé ». Défaut 96. */
  seuilBas?: number;
  /** Défaut « Chargement de l'historique… ». */
  libelleChargement?: ReactNode;
  /** En tête, quand tout est chargé : « Début de la conversation ». */
  debut?: ReactNode;
  /** Les non-lus, sur le bouton « revenir en bas ». */
  nonLus?: number | null;
  /** Défaut « Revenir en bas ». */
  libelleBas?: string;
  /** On vient de revenir en bas (marquer comme lu). */
  onBasAtteint?: () => void;
  /** Défaut « Messages ». */
  ariaLabel?: string;
  poignee?: Ref<PoigneeDuFil>;
  /** Tout à gauche (défaut) ou `deux-cotes` : le sortant à droite. */
  disposition?: DispositionDuFil;
  className?: string;
}

interface Ancre {
  el: Element;
  decalage: number;
}

function moinsDeMouvement(): boolean {
  return typeof window !== "undefined" && "matchMedia" in window && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function Fil({
  children,
  onPlusAncien,
  chargementAncien = false,
  toutCharge = false,
  seuilAncien = 800,
  seuilBas = 96,
  libelleChargement = "Chargement de l'historique…",
  debut,
  nonLus,
  libelleBas = "Revenir en bas",
  onBasAtteint,
  ariaLabel = "Messages",
  poignee,
  disposition = "a-gauche",
  className,
}: FilProps) {
  const defile = useRef<HTMLDivElement>(null);
  const contenu = useRef<HTMLDivElement>(null);
  const colle = useRef(true);
  const ancre = useRef<Ancre | null>(null);
  const demande = useRef(false);
  const [enBas, setEnBas] = useState(true);

  // Les props lues par les écouteurs, sans les réabonner à chaque rendu.
  const props = useRef({ onPlusAncien, chargementAncien, toutCharge, seuilAncien, seuilBas, onBasAtteint });
  props.current = { onPlusAncien, chargementAncien, toutCharge, seuilAncien, seuilBas, onBasAtteint };

  /** Retient la première rangée visible et sa distance au haut du fil. */
  const capturer = useCallback(() => {
    const d = defile.current;
    const c = contenu.current;
    if (!d || !c || colle.current) {
      ancre.current = null;
      return;
    }
    const haut = d.getBoundingClientRect().top;
    for (const enfant of Array.from(c.children)) {
      const r = enfant.getBoundingClientRect();
      if (r.bottom > haut) {
        ancre.current = { el: enfant, decalage: r.top - haut };
        return;
      }
    }
    ancre.current = null;
  }, []);

  const demander = useCallback(() => {
    const p = props.current;
    if (!p.onPlusAncien || p.chargementAncien || p.toutCharge || demande.current) return;
    demande.current = true;
    p.onPlusAncien();
  }, []);

  /** Après un changement de taille : collé → en bas ; sinon la rangée retenue reprend sa place. */
  const restaurer = useCallback(() => {
    const d = defile.current;
    if (!d) return;
    if (colle.current) {
      d.scrollTop = d.scrollHeight;
    } else if (ancre.current?.el.isConnected) {
      const haut = d.getBoundingClientRect().top;
      const decalage = ancre.current.el.getBoundingClientRect().top - haut;
      const ecart = decalage - ancre.current.decalage;
      if (Math.abs(ecart) > 0.5) d.scrollTop += ecart;
    }
    capturer();
    // un fil trop court pour défiler ne recevra jamais de défilement : il demande lui-même
    if (d.scrollHeight <= d.clientHeight + props.current.seuilAncien) demander();
  }, [capturer, demander]);

  // Le contenu change de taille (un message arrive, des anciens se posent au-dessus, une image charge).
  useLayoutEffect(() => {
    const c = contenu.current;
    if (!c) return;
    restaurer();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      demande.current = false;
      restaurer();
    });
    ro.observe(c);
    return () => ro.disconnect();
  }, [restaurer]);

  // Le chargement retombe (réussi ou non) : on pourra redemander.
  useEffect(() => {
    if (!chargementAncien) demande.current = false;
  }, [chargementAncien]);

  const surDefilement = () => {
    const d = defile.current;
    if (!d) return;
    const p = props.current;
    const distance = d.scrollHeight - d.scrollTop - d.clientHeight;
    const etait = colle.current;
    colle.current = distance <= p.seuilBas;
    if (colle.current !== etait) {
      setEnBas(colle.current);
      if (colle.current) p.onBasAtteint?.();
    }
    capturer();
    if (d.scrollTop < p.seuilAncien) demander();
  };

  const allerEnBas = useCallback((doux = true) => {
    const d = defile.current;
    if (!d) return;
    colle.current = true;
    ancre.current = null;
    setEnBas(true);
    d.scrollTo({ top: d.scrollHeight, behavior: doux && !moinsDeMouvement() ? "smooth" : "auto" });
  }, []);

  useImperativeHandle(
    poignee,
    () => ({
      allerEnBas,
      allerA: (id: string, doux = true) => {
        const d = defile.current;
        const el = d?.querySelector(`#${CSS.escape(id)}`);
        if (!d || !el) return false;
        colle.current = false;
        setEnBas(false);
        el.scrollIntoView({ block: "center", behavior: doux && !moinsDeMouvement() ? "smooth" : "auto" });
        return true;
      },
      element: () => defile.current,
    }),
    [allerEnBas],
  );

  const compte = nonLus !== undefined && nonLus !== null && nonLus > 0 ? (nonLus > 99 ? "99+" : String(nonLus)) : null;

  return (
    <div className={cn("cv-fil", className)}>
      <div
        ref={defile}
        className="cv-fil-defile"
        role="log"
        aria-label={ariaLabel}
        aria-busy={chargementAncien || undefined}
        tabIndex={0}
        onScroll={surDefilement}
      >
        <div ref={contenu} className="flex min-w-0 flex-col">
          {toutCharge && debut !== undefined && debut !== null && <div className="cv-fil-haut">{debut}</div>}
          <ContexteDisposition.Provider value={disposition}>{children}</ContexteDisposition.Provider>
        </div>
      </div>
      {chargementAncien && (
        <div className="pointer-events-none absolute inset-x-0 top-2 z-[6] flex justify-center">
          <span className="cv-fil-chargement" role="status">
            <Badge ton="gris" size="md" icon={<Spinner taille="sm" ton="courant" decoratif />}>
              {libelleChargement}
            </Badge>
          </span>
        </div>
      )}
      {!enBas && (
        <div className="cv-fil-bas">
          <Button
            variant="secondary"
            size="lg"
            iconOnly={!compte}
            className="h-11 min-w-11 gap-2 px-3 text-[14px] [&_svg]:h-5 [&_svg]:w-5"
            aria-label={compte ? `${libelleBas} · ${compte}` : libelleBas}
            title={libelleBas}
            iconRight={<ArrowDown aria-hidden />}
            onClick={() => allerEnBas()}
          >
            {compte}
          </Button>
        </div>
      )}
    </div>
  );
}
